import { useAtomValue } from "@effect/atom-react";
import { useEffect, useRef } from "react";

import type { MorrowSignalTick } from "@t3tools/client-runtime/state/morrowSignals";
import { activeMorrowSignalTickAtom } from "../state/morrowSignals";

/**
 * Fire `onSignal` whenever a fresh "something changed" poke of `kind` arrives
 * over the WebSocket (see the server `MorrowSignalHub`). Panels use this to
 * refetch on push instead of polling on a fast timer — a slow safety poll is
 * still kept as a backstop for a missed/dropped poke.
 *
 * Each poke carries a monotonically increasing `seq`, so a repeat of the same
 * kind still registers as a new signal. A null active environment simply never
 * ticks (the atom returns the initial seq 0), so callers degrade to their poll.
 */
export function useMorrowSignal(
  kind: MorrowSignalTick["kind"],
  onSignal: (tick: MorrowSignalTick) => void,
): void {
  const tick = useAtomValue(activeMorrowSignalTickAtom);
  const lastSeqRef = useRef(0);
  const cbRef = useRef(onSignal);
  cbRef.current = onSignal;
  useEffect(() => {
    if (tick.seq > lastSeqRef.current) {
      lastSeqRef.current = tick.seq;
      if (tick.kind === kind) cbRef.current(tick);
    }
  }, [tick, kind]);
}
